import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, TextInput, Checkbox, showSuccessToast, showErrorToast } from '@ui';
import I18n, { translate } from '@utils/i18n';
import api from '@utils/axios';
import { sendInvoiceEmailInvoicesPath } from '@routes';
import './SendInvoiceModalStyles.scss';
const namespace = 'features.invoices.index.table.actions.send_email';
const FORM_ID = 'invoice-send-form';
export default function SendInvoiceModal({ invoiceId, customerEmail, subject, body, pdfPath, hasLatestReport = false, defaultAttachPdf = false, defaultBccMe = false, isOpen, onClose, onSent }) {
    const [loading, setLoading] = useState(false);
    const [sendToEmail, setSendToEmail] = useState(customerEmail !== null && customerEmail !== void 0 ? customerEmail : '');
    const [emailSubject, setEmailSubject] = useState(subject !== null && subject !== void 0 ? subject : '');
    const [emailBody, setEmailBody] = useState(body !== null && body !== void 0 ? body : '');
    const [bccMe, setBccMe] = useState(defaultBccMe);
    const [attachPdf, setAttachPdf] = useState(defaultAttachPdf);
    const [addLatestReport, setAddLatestReport] = useState(false);
    // The show page keeps this component mounted and only flips `isOpen`, so without
    // this the tenant defaults would apply once per page load: override Attach PDF,
    // close, reopen, and the override would still be there. The list page mounts it
    // fresh each time and already resets, so the two surfaces disagreed. A tenant
    // default describes how each send starts, so re-apply it whenever the modal opens.
    //
    // The text fields are deliberately left alone — reopening should not discard an
    // edited subject or body, which matches the dialog this replaced.
    useEffect(() => {
        if (!isOpen)
            return;
        setBccMe(defaultBccMe);
        setAttachPdf(defaultAttachPdf);
        setAddLatestReport(false);
    }, [isOpen, defaultBccMe, defaultAttachPdf]);
    const isValid = sendToEmail.trim().length > 0 && emailSubject.trim().length > 0;
    const onSubmit = () => {
        if (!isValid || loading)
            return;
        setLoading(true);
        const payload = new FormData();
        payload.append('invoice_id', String(invoiceId));
        payload.append('send_to_email', sendToEmail);
        payload.append('email_subject', emailSubject);
        payload.append('email_body', emailBody);
        if (bccMe)
            payload.append('bcc_me', String(bccMe));
        if (attachPdf)
            payload.append('attach_pdf', String(attachPdf));
        if (addLatestReport)
            payload.append('add_lastest_generated_report', String(addLatestReport));
        api
            .post(sendInvoiceEmailInvoicesPath({ format: 'json' }), payload)
            .then(({ data }) => {
            showSuccessToast(data.message);
            onSent === null || onSent === void 0 ? void 0 : onSent();
            onClose();
        })
            .catch((error) => {
            var _a, _b, _c;
            showErrorToast((_c = (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : String(I18n.t('generic.errors.request_error')));
        })
            .finally(() => setLoading(false));
    };
    // Deliberately the browser's own PDF viewer rather than the `@ui` PDFViewer.
    // `<object>` brings page navigation, fit-to-width, rotate, download and print for
    // free; the Quimby viewer has none of those yet, so swapping it in here would be a
    // net loss of function for the person sending the invoice. The scaling defect this
    // ticket fixes lives in the dialog's sizing, not in the preview — see the plan doc.
    // Revisit once the Quimby viewer grows a toolbar.
    const pdfPreview = pdfPath ? (React.createElement("div", { className: "sendInvoiceModal__preview" },
        React.createElement("object", { data: pdfPath, type: "application/pdf", className: "sendInvoiceModal__pdf" },
            React.createElement("p", null,
                React.createElement("a", { href: pdfPath, target: "_blank", rel: "noopener noreferrer" }, translate('pdf_viewer_not_supported', { namespace })))))) : null;
    return (React.createElement(Modal, { isOpen: isOpen, onClose: onClose, variant: "base", className: "sendInvoiceModal" },
        React.createElement(ModalHeader, { title: translate('send_email', { namespace }), onClose: onClose }),
        React.createElement(ModalBody, { className: "sendInvoiceModal__body" },
            React.createElement("form", { id: FORM_ID, className: "sendInvoiceModal__form", onSubmit: (e) => {
                    e.preventDefault();
                    onSubmit();
                } },
                React.createElement(TextInput, { id: "invoice-send-email", type: "email", multiple: true, label: translate('email', { namespace }), required: true, value: sendToEmail, disabled: loading, onChange: (e) => setSendToEmail(e.target.value) }),
                React.createElement(TextInput, { id: "invoice-send-subject", label: translate('subject', { namespace }), required: true, value: emailSubject, disabled: loading, onChange: (e) => setEmailSubject(e.target.value) }),
                React.createElement(Checkbox, { id: "invoice-send-bcc-me", label: translate('send_me_copy', { namespace }), checked: bccMe, disabled: loading, onChange: setBccMe }),
                React.createElement(Checkbox, { id: "invoice-send-attach-pdf", label: translate('attach_pdf', { namespace }), checked: attachPdf, disabled: loading, onChange: setAttachPdf }),
                hasLatestReport && (React.createElement(Checkbox, { id: "invoice-send-add-latest-report", label: translate('add_latest_report', { namespace }), checked: addLatestReport, disabled: loading, onChange: setAddLatestReport })),
                React.createElement(TextInput, { id: "invoice-send-body", multiline: true, rows: 12, label: translate('body', { namespace }), value: emailBody, disabled: loading, onChange: (e) => setEmailBody(e.target.value) })),
            pdfPreview),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { onClick: onClose, disabled: loading }, I18n.t('generic.cancel')),
            React.createElement(Button, { variant: "primary", type: "submit", form: FORM_ID, disabled: loading || !isValid }, translate('send_and_close', { namespace })))));
}
